import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { JSX, ReactNode } from 'react';
import { useState } from 'react';

import { Button } from '../button/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../dropdown-menu/DropdownMenu';
import { IconChevronDownSmall, IconChevronTopSmall, IconCrossSmall } from '../icons';

type FilterMenuProps = {
    readonly activeCount?: number;
    readonly activeLabel?: ReactNode;
    readonly activeSummary?: ReactNode;
    readonly align?: 'center' | 'end' | 'start';
    readonly children: ReactNode;
    readonly clearLabel?: string;
    readonly className?: string;
    readonly content: ReactNode;
    readonly disabled?: boolean;
    readonly formatActiveCount?: (count: number) => ReactNode;
    readonly onClear?: () => void;
    readonly onOpenChange?: (open: boolean) => void;
};

const FILTER_ICON_SIZE = 12;

const getFilterMenuText = ({
    activeCount,
    activeLabel,
    activeSummary,
    children,
    formatActiveCount,
}: Pick<
    FilterMenuProps,
    'activeCount' | 'activeLabel' | 'activeSummary' | 'children' | 'formatActiveCount'
>): ReactNode => {
    const count = activeCount ?? 0;

    if (count <= 0) {
        return children;
    }

    if (!is.nullOrUndefined(activeSummary)) {
        return activeSummary;
    }

    if (count === 1 && !is.nullOrUndefined(activeLabel)) {
        return activeLabel;
    }

    return formatActiveCount?.(count) ?? String(count);
};

const getFilterMenuIcon = (isOpen: boolean): ReactNode => {
    if (isOpen) {
        return <IconChevronTopSmall size={FILTER_ICON_SIZE} />;
    }

    return <IconChevronDownSmall size={FILTER_ICON_SIZE} />;
};

const FilterMenuClearButton = ({
    clearLabel,
    onClear,
}: {
    readonly clearLabel: string;
    readonly onClear: () => void;
}): JSX.Element => (
    <button
        aria-label={clearLabel}
        className="absolute right-[calc(var(--spacing-m)+var(--spacing-xs)+12px)] top-1/2 inline-flex size-4 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-accent hover:bg-indicator-highlighted-surface hover:text-accent-hover focus-visible:outline focus-visible:outline-1 focus-visible:outline-focus-ring"
        title={clearLabel}
        type="button"
        onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onClear();
        }}
        onKeyDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
    >
        <IconCrossSmall size={FILTER_ICON_SIZE} />
    </button>
);

export const FilterMenu = ({
    activeCount = 0,
    activeLabel,
    activeSummary,
    align = 'start',
    children,
    clearLabel,
    className,
    content,
    disabled = false,
    formatActiveCount,
    onClear,
    onOpenChange,
}: FilterMenuProps): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);
    const hasValue = activeCount > 0;
    const buttonText = getFilterMenuText({ activeCount, activeLabel, activeSummary, children, formatActiveCount });

    const handleClear = (): void => {
        onClear?.();
    };

    const changeOpen = (open: boolean): void => {
        setIsOpen(open);
        onOpenChange?.(open);
    };

    const showClear = hasValue && !disabled && !is.undefined(onClear) && !is.undefined(clearLabel);

    return (
        <DropdownMenu open={isOpen} onOpenChange={changeOpen}>
            <div className="relative inline-flex min-w-0 max-w-[14rem]">
                <DropdownMenuTrigger>
                    <Button
                        className={clsx('min-w-0 max-w-[14rem]', className)}
                        disabled={disabled}
                        iconPosition="trailing"
                        isActive={hasValue}
                        size="sm"
                        variant="secondary"
                    >
                        <span className="min-w-0 max-w-[12rem] truncate">{buttonText}</span>
                        {showClear && <span aria-hidden={true} className="inline-flex size-4 shrink-0" />}
                        <span className="inline-flex size-3 shrink-0 items-center justify-center">
                            {getFilterMenuIcon(isOpen)}
                        </span>
                    </Button>
                </DropdownMenuTrigger>
                {showClear && !is.undefined(clearLabel) && (
                    <FilterMenuClearButton clearLabel={clearLabel} onClear={handleClear} />
                )}
            </div>
            <DropdownMenuContent align={align}>{content}</DropdownMenuContent>
        </DropdownMenu>
    );
};
